import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api from '@/api/engine';
import type { CreatorProfile } from '@/api/types';
import {
  DropdownSetting,
  EmptyState,
  PanelShell,
  PanelSkeleton,
  SettingsSection,
  SliderSetting,
  ToggleSetting,
  UpgradeNudge,
  normalizeError,
  useSettingsContext,
  type NormalizedError,
  type SaveStatus,
  type SettingVariant,
  type TierLevel,
} from '@seitiate/settings-ui';

// Persisted contract is {off, soft, hard}. The spec table's "advisory" value is a
// UX label that has no backend equivalent yet, so Lenient maps to `off` here.
type EnforcementMode = 'off' | 'soft' | 'hard';

interface CanonLearning {
  auto_learning_enabled: boolean;
  bootstrap_from_empty: boolean;
  promotion_alignment_threshold: number;
  duplicate_similarity_threshold: number;
  minimum_content_chars: number;
  minimum_content_words: number;
}

interface CanonCode {
  enforcement_mode: EnforcementMode;
  category_enforcement: Record<string, EnforcementMode>;
  embedding_weight: number;
  structural_weight: number;
  negative_evidence_weight: number;
  minimum_evidence_threshold: number;
  review_threshold: number;
  auto_capture_rejections: boolean;
}

// Metadata Model §reference_sets.canon_code_category_ids_v1.
const CANON_CODE_CATEGORIES = [
  'conventions',
  'architecture',
  'testing',
  'documentation',
  'error_handling',
  'api_design',
  'simplicity',
] as const;

const CATEGORY_LABELS: Record<string, string> = {
  conventions: 'Conventions',
  architecture: 'Architecture',
  testing: 'Testing',
  documentation: 'Documentation',
  error_handling: 'Error handling',
  api_design: 'API design',
  simplicity: 'Simplicity',
};

const ENFORCEMENT_OPTIONS = [
  { value: 'off', label: 'Off' },
  { value: 'soft', label: 'Soft' },
  { value: 'hard', label: 'Hard' },
];

const DEFAULT_LEARNING: CanonLearning = {
  auto_learning_enabled: true,
  bootstrap_from_empty: true,
  promotion_alignment_threshold: 0.75,
  duplicate_similarity_threshold: 0.92,
  minimum_content_chars: 80,
  minimum_content_words: 12,
};

const DEFAULT_CODE: CanonCode = {
  enforcement_mode: 'soft',
  category_enforcement: Object.fromEntries(
    CANON_CODE_CATEGORIES.map((c) => [c, 'soft']),
  ) as Record<string, EnforcementMode>,
  embedding_weight: 0.6,
  structural_weight: 0.2,
  negative_evidence_weight: 0.2,
  minimum_evidence_threshold: 3,
  review_threshold: 0.5,
  auto_capture_rejections: true,
};

type Preset = 'lenient' | 'balanced' | 'strict' | 'custom';

interface PresetPayload {
  learning: Partial<CanonLearning>;
  code: Partial<CanonCode>;
}

const PRESETS: Record<Exclude<Preset, 'custom'>, PresetPayload> = {
  lenient: {
    learning: {
      auto_learning_enabled: true,
      promotion_alignment_threshold: 0.65,
      duplicate_similarity_threshold: 0.95,
    },
    code: { enforcement_mode: 'off' },
  },
  balanced: {
    learning: {
      auto_learning_enabled: true,
      promotion_alignment_threshold: 0.75,
      duplicate_similarity_threshold: 0.92,
    },
    code: { enforcement_mode: 'soft' },
  },
  strict: {
    learning: {
      auto_learning_enabled: true,
      promotion_alignment_threshold: 0.85,
      duplicate_similarity_threshold: 0.9,
    },
    code: { enforcement_mode: 'hard' },
  },
};

const APPROX = (a: number, b: number) => Math.abs(a - b) < 0.001;

function detectPreset(learning: CanonLearning, code: CanonCode): Preset {
  for (const name of ['lenient', 'balanced', 'strict'] as const) {
    const p = PRESETS[name];
    const lm = p.learning;
    const cm = p.code;
    const matches =
      learning.auto_learning_enabled === lm.auto_learning_enabled &&
      APPROX(learning.promotion_alignment_threshold, lm.promotion_alignment_threshold as number) &&
      APPROX(learning.duplicate_similarity_threshold, lm.duplicate_similarity_threshold as number) &&
      code.enforcement_mode === cm.enforcement_mode;
    if (matches) return name;
  }
  return 'custom';
}

const FIRST_RUN_STORAGE_KEY = 'seitiate.settings.canon.firstRunSeen';
// Accepts the legacy TierLevel ladder AND the new Free / Amateur / Professional
// platform base (Option-B re-home). Canon is a Meaning+ feature; professional
// (the top platform tier) maps to mastery, so it clears the Meaning bar, while
// free/amateur stay below it — matching the prior sense/making gating.
const MEANING_TIER_RANK: Record<string, number> = {
  sense: 0,
  making: 1,
  meaning: 2,
  mastery: 3,
  sovereign: 4,
  free: 0,
  amateur: 1,
  professional: 3,
};

function meetsMeaningTier(tier: TierLevel | string | undefined): boolean {
  if (!tier) return true; // Undefined tier → don't gate on frontend; backend enforces.
  return (MEANING_TIER_RANK[tier] ?? 0) >= MEANING_TIER_RANK.meaning;
}

function titleCopy(variant: SettingVariant) {
  return variant === 'creator' ? 'Your Taste' : 'Canon';
}

function descriptionCopy(variant: SettingVariant) {
  return variant === 'creator'
    ? 'Calibrate how your seity learns and enforces your taste.'
    : 'Canon Learning thresholds and Canon Code enforcement weights.';
}

function firstRunBodyCopy(variant: SettingVariant): string {
  if (variant === 'creator') {
    return (
      "Your seity is learning your taste. Every time you approve or reject a generation — or when your Canon picker promotes an observation — Canon Learning captures a taste signal. " +
      "Those signals turn into Canon assertions: named preferences your seity will check against future generations. " +
      "Canon Learning is on by default. You can turn it off here; your seity will stop capturing new signals but existing assertions stay unless you explicitly remove them."
    );
  }
  return (
    'Canon Learning promotes observations into assertions when signal exceeds the alignment threshold. ' +
    'Canon Code then scores generations against those assertions using embedding / structural / negative-evidence weights. ' +
    'Toggle off to halt promotion without dropping existing assertions.'
  );
}

export const CanonPanel: React.FC = () => {
  const { variant, tier, expertMode } = useSettingsContext();
  const tierOk = meetsMeaningTier(tier);

  const [learning, setLearning] = useState<CanonLearning>(DEFAULT_LEARNING);
  const [code, setCode] = useState<CanonCode>(DEFAULT_CODE);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<NormalizedError | null>(null);
  const [saveStatus, setSaveStatus] = useState<SaveStatus>({ state: 'idle' });
  const [profile, setProfile] = useState<CreatorProfile | null>(null);
  const [firstRunSeen, setFirstRunSeen] = useState<boolean>(() => {
    if (typeof window === 'undefined') return true;
    try {
      return window.localStorage.getItem(FIRST_RUN_STORAGE_KEY) === 'true';
    } catch {
      return true;
    }
  });

  const markFirstRunSeen = useCallback(() => {
    setFirstRunSeen(true);
    try {
      window.localStorage.setItem(FIRST_RUN_STORAGE_KEY, 'true');
    } catch {
      /* quota / privacy-mode — ignore */
    }
  }, []);

  const reopenExplainer = useCallback(() => {
    setFirstRunSeen(false);
  }, []);

  const loadAll = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const [learningRes, codeRes] = await Promise.all([
        api.settings.getCategory('canon_learning'),
        api.settings.getCategory('canon_code'),
      ]);
      setLearning({ ...DEFAULT_LEARNING, ...(learningRes.settings as Partial<CanonLearning>) });
      setCode({
        ...DEFAULT_CODE,
        ...(codeRes.settings as Partial<CanonCode>),
        category_enforcement: {
          ...DEFAULT_CODE.category_enforcement,
          ...((codeRes.settings as Partial<CanonCode>).category_enforcement ?? {}),
        },
      });
    } catch (err) {
      setLoadError(normalizeError(err));
    } finally {
      setLoading(false);
    }
    // Transparency line — best-effort; absence is graceful.
    try {
      const mirror = await api.creator.getProfile();
      setProfile(mirror);
    } catch {
      setProfile(null);
    }
  }, []);

  useEffect(() => {
    if (!tierOk) return;
    void loadAll();
  }, [loadAll, tierOk]);

  // UX State Doctrine §3.4: no optimistic updates. Issue write, reconcile from response.
  const saveLearning = useCallback(async (patch: Partial<CanonLearning>) => {
    setSaveStatus({ state: 'saving' });
    try {
      const response = await api.settings.updateCategory('canon_learning', patch);
      setLearning((prev) => ({ ...prev, ...(response.settings as Partial<CanonLearning>) }));
      setSaveStatus({ state: 'saved', at: Date.now() });
    } catch (err) {
      const normalized = normalizeError(err);
      setSaveStatus({
        state: 'failed',
        message: normalized.message,
        onRetry: () => void saveLearning(patch),
      });
    }
  }, []);

  const saveCode = useCallback(async (patch: Partial<CanonCode>) => {
    setSaveStatus({ state: 'saving' });
    try {
      const response = await api.settings.updateCategory('canon_code', patch);
      setCode((prev) => {
        const next = { ...prev, ...(response.settings as Partial<CanonCode>) };
        const serverCats = (response.settings as Partial<CanonCode>).category_enforcement;
        if (serverCats) {
          next.category_enforcement = { ...prev.category_enforcement, ...serverCats };
        }
        return next;
      });
      setSaveStatus({ state: 'saved', at: Date.now() });
    } catch (err) {
      const normalized = normalizeError(err);
      setSaveStatus({
        state: 'failed',
        message: normalized.message,
        onRetry: () => void saveCode(patch),
      });
    }
  }, []);

  const updateLearning = <K extends keyof CanonLearning>(key: K, value: CanonLearning[K]) => {
    void saveLearning({ [key]: value } as Partial<CanonLearning>);
  };

  const updateCode = <K extends keyof CanonCode>(key: K, value: CanonCode[K]) => {
    void saveCode({ [key]: value } as Partial<CanonCode>);
  };

  const updateCategoryEnforcement = (category: string, value: EnforcementMode) => {
    void saveCode({
      category_enforcement: {
        ...code.category_enforcement,
        [category]: value,
      },
    });
  };

  const currentPreset = useMemo(() => detectPreset(learning, code), [learning, code]);

  const applyPreset = useCallback(
    (next: Preset) => {
      if (next === 'custom') return;
      const payload = PRESETS[next];
      const hasCustomized = currentPreset === 'custom';
      if (hasCustomized) {
        const confirmed = window.confirm(
          'Switching presets will overwrite your customized Canon thresholds. Continue?',
        );
        if (!confirmed) return;
      }
      if (payload.learning && Object.keys(payload.learning).length > 0) {
        void saveLearning(payload.learning);
      }
      if (payload.code && Object.keys(payload.code).length > 0) {
        void saveCode(payload.code);
      }
    },
    [currentPreset, saveLearning, saveCode],
  );

  const weightSum =
    code.embedding_weight + code.structural_weight + code.negative_evidence_weight;
  const weightSumWarn = weightSum > 1.0;

  const assertionCount = profile?.total_codex_chunks ?? 0;
  const showDriftViz = expertMode && assertionCount >= 10;

  // Tier gate — full-panel UpgradeNudge for Sense / Making (§1 Tier gate).
  if (!tierOk) {
    return (
      <PanelShell
        title={titleCopy(variant)}
        description={descriptionCopy(variant)}
        tierBadge="Meaning+"
        showExpertToggle
      >
        <UpgradeNudge requiredTier="Meaning" feature="Canon calibration" />
      </PanelShell>
    );
  }

  if (loading) {
    return (
      <PanelShell title={titleCopy(variant)} description={descriptionCopy(variant)} tierBadge="Meaning+">
        <PanelSkeleton rows={5} />
      </PanelShell>
    );
  }

  if (loadError) {
    return (
      <PanelShell
        title={titleCopy(variant)}
        description={descriptionCopy(variant)}
        tierBadge="Meaning+"
        error={loadError}
        onRetryError={() => void loadAll()}
      >
        <div />
      </PanelShell>
    );
  }

  return (
    <PanelShell
      title={titleCopy(variant)}
      description={descriptionCopy(variant)}
      tierBadge="Meaning+"
      showExpertToggle
      showRefresh
      onRefresh={() => void loadAll()}
      saveStatus={saveStatus}
    >
      {/* §0 — First-run explainer + primary opt-out (Decision 5 condition). Returning users
          see the collapsed one-line summary with a chevron to re-expand. */}
      {!firstRunSeen ? (
        <section
          style={{
            background: 'var(--sp-bg-elev-2)',
            border: '1px solid var(--sp-border)',
            borderRadius: 'var(--sp-radius)',
            padding: 'var(--sp-space-4)',
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--sp-space-3)',
          }}
          aria-label="Canon Learning explainer"
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
            <h3
              style={{
                margin: 0,
                fontSize: 11,
                fontFamily: 'var(--sp-font-mono)',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                color: 'var(--sp-text)',
              }}
            >
              {variant === 'creator' ? 'What is Canon Learning?' : 'Canon Learning overview'}
            </h3>
            <button
              type="button"
              onClick={markFirstRunSeen}
              className="sp-focus-ring"
              style={{
                fontSize: 10,
                fontFamily: 'var(--sp-font-mono)',
                color: 'var(--sp-text-muted)',
                background: 'transparent',
                border: '1px solid var(--sp-border-strong)',
                borderRadius: 'var(--sp-radius)',
                padding: '2px 8px',
                cursor: 'pointer',
              }}
              aria-label="Collapse explainer"
            >
              Collapse
            </button>
          </div>
          <p style={{ margin: 0, fontSize: 13, color: 'var(--sp-text-muted)', lineHeight: 1.6 }}>
            {firstRunBodyCopy(variant)}
          </p>
          {/* §0 — primary opt-out toggle. Writes same field as §B Advanced duplicate. */}
          <ToggleSetting
            anchorId="canon_learning-auto_learning_enabled"
            label={variant === 'creator' ? 'Canon Learning on' : 'Auto-learning enabled'}
            checked={learning.auto_learning_enabled}
            onChange={(value) => updateLearning('auto_learning_enabled', value)}
            description={
              variant === 'creator'
                ? "Your seity captures taste signals as you approve or reject generations."
                : 'Promotes qualifying observations into assertions when similarity exceeds threshold.'
            }
          />
        </section>
      ) : (
        <button
          type="button"
          onClick={reopenExplainer}
          aria-expanded={false}
          aria-controls="canon-learning-explainer-summary"
          aria-label="Expand Canon Learning explainer"
          className="sp-focus-ring"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '100%',
            gap: 12,
            background: 'var(--sp-bg-elev-1)',
            border: '1px solid var(--sp-border)',
            borderRadius: 'var(--sp-radius)',
            padding: 'var(--sp-space-2) var(--sp-space-3)',
            color: 'var(--sp-text-muted)',
            fontSize: 12,
            fontFamily: 'var(--sp-font-mono)',
            cursor: 'pointer',
            textAlign: 'left',
          }}
        >
          <span id="canon-learning-explainer-summary">
            Canon Learning is{' '}
            <strong style={{ color: 'var(--sp-text)' }}>
              {learning.auto_learning_enabled ? 'on' : 'off'}
            </strong>
            . What is this?
          </span>
          <span aria-hidden="true" style={{ fontSize: 10 }}>▾</span>
        </button>
      )}

      {/* Transparency line — always visible, sits above the Preset section per spec §0. */}
      <p
        style={{
          margin: 0,
          fontSize: 10,
          color: 'var(--sp-text-dim)',
          fontFamily: 'var(--sp-font-mono)',
        }}
      >
        Current Canon: {assertionCount} codex chunks ·{' '}
        <a href="/settings/memory" style={{ color: 'var(--sp-accent)' }}>
          see all in Memory
        </a>
      </p>

      {/* §A — Preset (Simple, always visible) */}
      <SettingsSection title="Preset" anchorId="section-taste-canon-preset">
        <DropdownSetting
          label={variant === 'creator' ? 'Canon preset' : 'Preset'}
          value={currentPreset}
          options={[
            { value: 'lenient', label: 'Lenient' },
            { value: 'balanced', label: 'Balanced' },
            { value: 'strict', label: 'Strict' },
            { value: 'custom', label: 'Custom' },
          ]}
          onChange={(v) => applyPreset(v as Preset)}
          description={
            variant === 'creator'
              ? 'Quick calibration. Switch to a preset, or tune each field below.'
              : 'Overwrites promotion / duplicate / enforcement when switching to a non-custom preset.'
          }
        />
      </SettingsSection>

      {/* §B — Canon Learning (Advanced) */}
      <SettingsSection title="Canon Learning" defaultExpanded={false} badge="ADVANCED">
        <ToggleSetting
          anchorId="canon_learning-auto_learning_enabled"
          label={variant === 'creator' ? 'Canon Learning on' : 'Auto-learning'}
          checked={learning.auto_learning_enabled}
          onChange={(value) => updateLearning('auto_learning_enabled', value)}
          description={
            variant === 'creator'
              ? 'Capture taste signals from your approvals and rejections.'
              : 'Gate on signal capture → assertion promotion pipeline.'
          }
        />
        <ToggleSetting
          anchorId="canon_learning-bootstrap_from_empty"
          label={variant === 'creator' ? 'Bootstrap from empty' : 'Bootstrap from empty'}
          checked={learning.bootstrap_from_empty}
          onChange={(value) => updateLearning('bootstrap_from_empty', value)}
          description={
            variant === 'creator'
              ? 'Allow learning to kick off before any assertions exist.'
              : 'Permit promotion when assertion set is empty (cold-start).'
          }
        />
        <SliderSetting
          anchorId="canon_learning-promotion_alignment_threshold"
          label={
            variant === 'creator'
              ? 'Promotion threshold'
              : 'Promotion alignment threshold'
          }
          value={learning.promotion_alignment_threshold}
          min={0.5}
          max={0.95}
          step={0.01}
          formatValue={(v) => v.toFixed(2)}
          onChange={(v) => updateLearning('promotion_alignment_threshold', v)}
          description={
            variant === 'creator'
              ? "How closely an observation has to match your established taste before it's promoted to a Canon assertion. Higher = stricter."
              : 'Cosine similarity threshold for promoting observation to assertion. Range 0.50–0.95.'
          }
        />
        <SliderSetting
          anchorId="canon_learning-duplicate_similarity_threshold"
          label={
            variant === 'creator'
              ? 'Duplicate threshold'
              : 'Duplicate similarity threshold'
          }
          value={learning.duplicate_similarity_threshold}
          min={0.75}
          max={0.99}
          step={0.01}
          formatValue={(v) => v.toFixed(2)}
          onChange={(v) => updateLearning('duplicate_similarity_threshold', v)}
          description={
            variant === 'creator'
              ? 'How close two observations must be before they are treated as duplicates.'
              : 'Above this similarity, incoming observation is merged rather than promoted.'
          }
        />
        <SliderSetting
          anchorId="canon_learning-minimum_content_chars"
          label={variant === 'creator' ? 'Minimum characters' : 'Minimum content chars'}
          value={learning.minimum_content_chars}
          min={20}
          max={1000}
          step={10}
          unit="chars"
          onChange={(v) => updateLearning('minimum_content_chars', v)}
          description={
            variant === 'creator'
              ? 'Shorter snippets are ignored during learning.'
              : 'Reject observations below this character count.'
          }
        />
        <SliderSetting
          anchorId="canon_learning-minimum_content_words"
          label={variant === 'creator' ? 'Minimum words' : 'Minimum content words'}
          value={learning.minimum_content_words}
          min={3}
          max={200}
          step={1}
          unit="words"
          onChange={(v) => updateLearning('minimum_content_words', v)}
          description={
            variant === 'creator'
              ? 'Shorter snippets are ignored during learning.'
              : 'Reject observations below this word count.'
          }
        />
      </SettingsSection>

      {/* §C — Canon Code (Advanced) */}
      <SettingsSection title="Canon Code" defaultExpanded={false} badge="ADVANCED">
        <DropdownSetting
          anchorId="canon_code-enforcement_mode"
          label={variant === 'creator' ? 'How strictly to enforce Canon' : 'Enforcement mode'}
          value={code.enforcement_mode}
          options={ENFORCEMENT_OPTIONS}
          onChange={(v) => updateCode('enforcement_mode', v as EnforcementMode)}
          description={
            variant === 'creator'
              ? 'Off: no Canon checks. Soft: warn only. Hard: block drift.'
              : 'Top-level switch. Per-category overrides live below.'
          }
        />
        <SettingsSection
          title="Per-category enforcement"
          defaultExpanded={false}
          anchorId="canon_code-category_enforcement"
        >
          {CANON_CODE_CATEGORIES.map((cat) => (
            <DropdownSetting
              key={cat}
              anchorId={`canon_code-category_enforcement-${cat}`}
              label={CATEGORY_LABELS[cat]}
              value={code.category_enforcement[cat] ?? 'soft'}
              options={ENFORCEMENT_OPTIONS}
              onChange={(v) => updateCategoryEnforcement(cat, v as EnforcementMode)}
            />
          ))}
        </SettingsSection>
        <SliderSetting
          anchorId="canon_code-embedding_weight"
          label={variant === 'creator' ? 'Embedding weight' : 'Embedding weight'}
          value={code.embedding_weight}
          min={0}
          max={1}
          step={0.05}
          formatValue={(v) => v.toFixed(2)}
          onChange={(v) => updateCode('embedding_weight', v)}
          description={
            variant === 'creator'
              ? 'Weight on semantic similarity when scoring a draft against Canon.'
              : 'Semantic similarity contribution to Canon score.'
          }
        />
        <SliderSetting
          anchorId="canon_code-structural_weight"
          label={variant === 'creator' ? 'Structural weight' : 'Structural weight'}
          value={code.structural_weight}
          min={0}
          max={1}
          step={0.05}
          formatValue={(v) => v.toFixed(2)}
          onChange={(v) => updateCode('structural_weight', v)}
          description={
            variant === 'creator'
              ? 'Weight on structural / form matches (beats, sections).'
              : 'Rule / form conformance contribution to Canon score.'
          }
        />
        <SliderSetting
          anchorId="canon_code-negative_evidence_weight"
          label={
            variant === 'creator'
              ? 'Negative-evidence weight'
              : 'Negative-evidence weight'
          }
          value={code.negative_evidence_weight}
          min={0}
          max={1}
          step={0.05}
          formatValue={(v) => v.toFixed(2)}
          onChange={(v) => updateCode('negative_evidence_weight', v)}
          description={
            variant === 'creator'
              ? 'Weight on past rejections as evidence against a draft.'
              : 'Prior-rejection evidence contribution to Canon score.'
          }
        />
        {weightSumWarn && (
          <p
            role="note"
            style={{
              margin: 0,
              fontSize: 10,
              color: 'var(--sp-warn)',
              fontFamily: 'var(--sp-font-mono)',
            }}
          >
            Weights sum to {weightSum.toFixed(2)} — governor may weight some signals twice.
          </p>
        )}
        <SliderSetting
          anchorId="canon_code-minimum_evidence_threshold"
          label={
            variant === 'creator'
              ? 'Minimum evidence'
              : 'Minimum evidence threshold'
          }
          value={code.minimum_evidence_threshold}
          min={1}
          max={20}
          step={1}
          onChange={(v) => updateCode('minimum_evidence_threshold', v)}
          description={
            variant === 'creator'
              ? 'Count of assertions needed before Canon Code starts enforcing.'
              : 'Lower bound of assertions before enforcement activates.'
          }
        />
        <SliderSetting
          anchorId="canon_code-review_threshold"
          label={variant === 'creator' ? 'Review threshold' : 'Review threshold'}
          value={code.review_threshold}
          min={0.2}
          max={0.95}
          step={0.01}
          formatValue={(v) => v.toFixed(2)}
          onChange={(v) => updateCode('review_threshold', v)}
          description={
            variant === 'creator'
              ? 'Canon score below this triggers review before publishing.'
              : 'Canon score floor for auto-pass; below → review queue.'
          }
        />
        <ToggleSetting
          anchorId="canon_code-auto_capture_rejections"
          label={
            variant === 'creator'
              ? 'Capture rejections automatically'
              : 'Auto-capture rejections'
          }
          checked={code.auto_capture_rejections}
          onChange={(v) => updateCode('auto_capture_rejections', v)}
          description={
            variant === 'creator'
              ? 'Your rejections become negative-evidence signals.'
              : 'Convert rejections into negative-evidence entries automatically.'
          }
        />
      </SettingsSection>

      {/* §D — Taste drift visualization (Expert, gated on assertion count + blueprint-planned endpoint) */}
      {showDriftViz && (
        <SettingsSection
          title="Taste drift"
          defaultExpanded={false}
          badge="EXPERT"
          anchorId="section-taste-canon-drift"
        >
          {/* Primitive state='blueprint-planned' hides in production unless internalDevSurface=true.
              Uses a SliderSetting as a read-only placeholder — real charts will replace when
              /api/v1/canon/metrics?days=30 ships (status: planned-not-yet-built). */}
          <SliderSetting
            label="Assertions captured (30d)"
            value={0}
            min={0}
            max={100}
            onChange={() => undefined}
            state="blueprint-planned"
            description="Bar chart of assertion promotions over the last 30 days."
          />
          <SliderSetting
            label="Alignment trend (7d avg)"
            value={0}
            min={0}
            max={1}
            step={0.01}
            onChange={() => undefined}
            state="blueprint-planned"
            description="Sparkline of canon_score rolling average."
          />
          <DropdownSetting
            label="Top rejection reasons"
            value=""
            options={[]}
            onChange={() => undefined}
            state="blueprint-planned"
            description="Top-5 reasons Canon Code has rejected outputs in the last 30 days."
          />
        </SettingsSection>
      )}

      {/* Empty state — surfaced when learning is on but no assertions exist. */}
      {learning.auto_learning_enabled && assertionCount === 0 && (
        <EmptyState
          title={
            variant === 'creator'
              ? "Your seity hasn't captured any taste assertions yet."
              : 'No Canon assertions captured yet.'
          }
          description={
            variant === 'creator'
              ? 'Once you approve or reject a generation, calibration begins.'
              : 'Approvals / rejections will populate the assertion store.'
          }
        />
      )}
    </PanelShell>
  );
};
